import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { PageHeader, Card } from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import { simulate, SimulationPayload, EngineErrorBody, Dow, RaceMode } from '@/api/engineService';
import type { GenerationCheck } from '@/api/plansService';
import SimulatorFilters, { FilterState } from './SimulatorFilters';
import RecapCard from './RecapCard';
import WeekCalendar from './WeekCalendar';
import { addDaysIso, settingsOf, todayIso } from './simulatorUtils';

const PHASE_LABELS: Record<string, string> = { PLAN: 'Plan', BACKUP: 'Attente', RECOVERY: 'Récupération' };

const fromParams = (params: URLSearchParams): FilterState => ({
  planId: params.get('planId') ?? '',
  today: params.get('today') ?? todayIso(),
  competitionDate: params.get('competitionDate') ?? '',
  mode: (params.get('mode') as RaceMode | null) ?? 'ENROLL',
  recoveryEndsOn: params.get('recoveryEndsOn') ?? '',
  sessionsPerWeek: Object.fromEntries(
    (params.get('n')?.split(',').filter(Boolean) ?? []).map((pair) => {
      const [sportId, n] = pair.split(':');
      return [sportId, Number(n)];
    })
  ),
  includePpg: params.get('ppg') === '1',
  days: params.get('days')?.split(',').filter(Boolean) ?? [],
  brickDow: params.get('brickDow') ? (Number(params.get('brickDow')) as Dow) : null,
});

const toParams = (state: FilterState): Record<string, string> => {
  const out: Record<string, string> = {};
  if (state.planId) out.planId = state.planId;
  if (state.today) out.today = state.today;
  if (state.competitionDate) out.competitionDate = state.competitionDate;
  if (state.mode !== 'ENROLL') out.mode = state.mode;
  if (state.recoveryEndsOn) out.recoveryEndsOn = state.recoveryEndsOn;
  const n = Object.entries(state.sessionsPerWeek);
  if (n.length > 0) out.n = n.map(([sportId, value]) => `${sportId}:${value}`).join(',');
  if (state.includePpg) out.ppg = '1';
  if (state.days.length > 0) out.days = state.days.join(',');
  if (state.brickDow) out.brickDow = String(state.brickDow);
  return out;
};

const SimulatorPage = () => {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => fromParams(params), [params]);
  const [check, setCheck] = useState<GenerationCheck | undefined>();
  const [result, setResult] = useState<SimulationPayload | null>(null);
  const [error, setError] = useState<EngineErrorBody | null>(null);
  const [showDropped, setShowDropped] = useState(false);

  const onChange = (next: FilterState) => setParams(toParams(next), { replace: true });
  const onCheckLoaded = useCallback((c: GenerationCheck | undefined) => setCheck(c), []);

  const run = useMutation({
    mutationFn: () =>
      simulate({
        planId: state.planId,
        competitionDate: check?.planType === 'spe' ? state.competitionDate : undefined,
        today: state.today,
        mode: state.mode,
        earliestStart: state.recoveryEndsOn ? addDaysIso(state.recoveryEndsOn, 1) : undefined,
        settings: check?.dayMode === 'FIXED' ? undefined : settingsOf(state),
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (err) => {
      setError((err as { response?: { data?: EngineErrorBody } })?.response?.data ?? { message: 'Simulation impossible.' });
    },
  });

  return (
    <>
      <PageHeader title="Simulateur" description="Entraînement / Simulateur de génération" />
      <div className="flex flex-col gap-4">
        <SimulatorFilters state={state} onChange={onChange} onSimulate={() => run.mutate()} simulating={run.isPending} error={error} onCheckLoaded={onCheckLoaded} />

        {!result && !run.isPending && (
          <Card>
            <p className="text-sm text-gray-500">Choisissez un plan et ses paramètres, puis lancez la simulation.</p>
          </Card>
        )}

        {result && (
          <div className={clsx('flex flex-col gap-4', run.isPending && 'opacity-50')}>
            <RecapCard
              sim={result}
              planName={result.recap.planName}
              formatCode={result.recap.formatCode}
              planType={result.recap.planType}
              competitionDate={result.recap.competitionDate}
              onShowDropped={() => setShowDropped(true)}
            />
            <WeekCalendar weeks={result.weeks} competitionDate={result.recap.competitionDate} competitionCoachNote={result.competitionCoachNote} showDropped={showDropped} onShowDroppedChange={setShowDropped} />
            {result.planTrace.length > 0 && (
              <Card>
                <p className="mb-1 text-xs font-medium uppercase text-gray-500">Décisions du plan</p>
                <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                  {result.planTrace
                    .filter((t) => t.code.startsWith('SITUATION'))
                    .map((t, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Badge color={t.phase === 'PLAN' ? 'blue' : 'gray'}>{PHASE_LABELS[t.phase ?? 'PLAN']}</Badge>
                        {t.message}
                      </li>
                    ))}
                </ul>
              </Card>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SimulatorPage;
